import { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Loader2, CheckCircle2, Circle, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const SHIFTS = ["apertura", "chiusura", "servizio"];

export default function HaccpPage() {
  const { locationId } = useAuth();
  const user = useUser();
  const { t } = useLang();
  const [templates, setTemplates] = useState([]);
  const [checks, setChecks] = useState([]);
  const [running, setRunning] = useState(null); // template
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", shift: "apertura", items: "" });
  const canManage = ["owner", "manager", "haccp_manager"].includes(user?.role);

  const load = useCallback(() => {
    const params = { location_id: locationId };
    api.get("/haccp/templates", { params }).then((r) => setTemplates(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/haccp/checks", { params }).then((r) => setChecks(r.data)).catch(() => {});
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("sk:data-changed", h);
    return () => window.removeEventListener("sk:data-changed", h);
  }, [load]);

  const startRun = (tpl) => {
    setRunning(tpl);
    setResults(Object.fromEntries(tpl.items.map((it) => [it, { done: false, note: "" }])));
  };

  const complete = async () => {
    setBusy(true);
    try {
      const payload = {
        template_id: running.template_id,
        results: running.items.map((it) => ({ item: it, done: results[it]?.done || false, note: results[it]?.note || "" })),
        location_id: locationId,
      };
      const r = await api.post("/haccp/checks", payload);
      if (r.data.anomalies_count > 0) {
        toast.warning(`${r.data.anomalies_count} ${t("haccp.anomalies")}`);
      } else {
        toast.success(t("common.success"));
      }
      setRunning(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const saveTemplate = async () => {
    try {
      await api.post("/haccp/templates", {
        name: tplForm.name, shift: tplForm.shift,
        items: tplForm.items.split("\n").map((s) => s.trim()).filter(Boolean),
        location_id: locationId,
      });
      toast.success(t("common.success"));
      setDialog(false);
      setTplForm({ name: "", shift: "apertura", items: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTemplate = async (tpl) => {
    try {
      await api.delete(`/haccp/templates/${tpl.template_id}`);
      toast.success(t("common.success"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (running) {
    const doneCount = Object.values(results).filter((r) => r.done).length;
    return (
      <div className="max-w-2xl mx-auto space-y-5 md:pl-14" data-testid="haccp-run-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{running.shift}</p>
            <h1 className="font-display text-2xl font-extrabold">{running.name}</h1>
          </div>
          <span className="text-sm font-mono font-bold">{doneCount}/{running.items.length}</span>
        </div>
        <p className="text-xs text-muted-foreground">{t("haccp.estTime")}: ~2 min</p>
        <div className="space-y-2">
          {running.items.map((item, i) => {
            const done = results[item]?.done;
            return (
              <div key={i} data-testid={`haccp-check-item-${i}`}
                className={`bg-card border-2 rounded-2xl p-4 transition-colors ${done ? "border-emerald-300 bg-emerald-50/50" : "border-border"}`}>
                <button
                  data-testid={`haccp-check-toggle-${i}`}
                  onClick={() => setResults({ ...results, [item]: { ...results[item], done: !done } })}
                  className="tactile-btn w-full flex items-center gap-3 text-left"
                >
                  {done ? <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" /> : <Circle className="h-7 w-7 text-muted-foreground shrink-0" />}
                  <span className={`font-semibold ${done ? "text-emerald-800" : ""}`}>{item}</span>
                </button>
                {!done && (
                  <Input data-testid={`haccp-check-note-${i}`} placeholder={t("products.note")}
                    value={results[item]?.note || ""}
                    onChange={(e) => setResults({ ...results, [item]: { ...results[item], note: e.target.value } })}
                    className="h-10 mt-2 ml-10 w-auto" />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 sticky bottom-20 md:bottom-4">
          <Button data-testid="haccp-complete-button" onClick={complete} disabled={busy}
            className="flex-1 h-14 text-base bg-emerald-600 hover:bg-emerald-700 tactile-btn font-bold">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("haccp.complete")}
          </Button>
          <Button data-testid="haccp-cancel-button" variant="outline" onClick={() => setRunning(null)} className="h-14">
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:pl-14" data-testid="haccp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("haccp.title")}</h1>
        {canManage && (
          <Button data-testid="new-template-button" variant="outline" onClick={() => setDialog(true)} className="h-11 tactile-btn">
            <Plus className="h-4 w-4 mr-1" />{t("haccp.newTemplate")}
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-xl p-3" data-testid="haccp-disclaimer">
        <Info className="h-4 w-4 text-violet-700 shrink-0 mt-0.5" />
        <p className="text-xs text-violet-800">{t("haccp.disclaimer")}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="haccp-templates">
        {templates.map((tpl) => (
          <div key={tpl.template_id} data-testid={`template-card-${tpl.template_id}`}
            className="bg-card border rounded-2xl p-5 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-violet-600 flex items-center justify-center shrink-0">
                  <ClipboardCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display font-bold">{tpl.name}</h2>
                  <p className="text-xs text-muted-foreground uppercase font-semibold">{t(`haccp.${tpl.shift === "apertura" ? "opening" : tpl.shift === "chiusura" ? "closing" : "service"}`)}</p>
                </div>
              </div>
              {canManage && (
                <button data-testid={`template-delete-${tpl.template_id}`} onClick={() => removeTemplate(tpl)}
                  className="tactile-btn h-9 w-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              {tpl.items.slice(0, 4).map((it, i) => <li key={i} className="flex gap-2"><span className="text-violet-500">•</span>{it}</li>)}
              {tpl.items.length > 4 && <li className="text-xs">+{tpl.items.length - 4}…</li>}
            </ul>
            <Button data-testid={`run-check-${tpl.template_id}`} onClick={() => startRun(tpl)}
              className="w-full h-12 bg-violet-600 hover:bg-violet-700 tactile-btn font-bold">
              {t("haccp.runCheck")}
            </Button>
          </div>
        ))}
      </div>

      <div className="bg-card border rounded-2xl p-5" data-testid="haccp-recent-checks">
        <h2 className="font-display font-bold mb-3">{t("haccp.recentChecks")}</h2>
        <div className="space-y-2">
          {checks.map((c) => (
            <div key={c.check_id} data-testid={`check-record-${c.check_id}`}
              className="flex items-center gap-3 flex-wrap border-b last:border-0 pb-2">
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${c.anomalies_count > 0 ? "bg-amber-500" : "bg-emerald-600"}`} />
              <p className="font-semibold text-sm flex-1 min-w-32">{c.template_name}</p>
              {c.anomalies_count > 0 && (
                <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-amber-100 text-amber-800">
                  {c.anomalies_count} {t("haccp.anomalies")}
                </span>
              )}
              <p className="text-xs text-muted-foreground">{c.user_name}</p>
              <p className="text-xs text-muted-foreground font-mono">{format(new Date(c.completed_at), "dd/MM/yyyy HH:mm")}</p>
              <button data-testid={`check-delete-${c.check_id}`} title={t("common.delete")}
                onClick={async () => {
                  if (!window.confirm(t("common.confirmDelete"))) return;
                  try { await api.delete(`/haccp/checks/${c.check_id}`); toast.success(t("common.success")); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}
                className="tactile-btn h-9 w-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-600 shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!checks.length && <p className="text-sm text-muted-foreground">{t("notif.empty")}</p>}
        </div>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-sm" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("haccp.newTemplate")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("products.name")}</Label>
              <Input data-testid="template-name" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("haccp.shift")}</Label>
              <select data-testid="template-shift" value={tplForm.shift} onChange={(e) => setTplForm({ ...tplForm, shift: e.target.value })}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm">
                {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("haccp.items")}</Label>
              <Textarea data-testid="template-items" rows={5} value={tplForm.items}
                onChange={(e) => setTplForm({ ...tplForm, items: e.target.value })}
                placeholder={"Temperatura frigoriferi\nPulizia superfici\n…"} />
            </div>
            <Button data-testid="template-save" onClick={saveTemplate} disabled={!tplForm.name || !tplForm.items.trim()}
              className="w-full h-12 tactile-btn font-bold">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
